/**
 * GlobalSearch — Cmd/Ctrl+K modal for projects, pay apps, and subs/vendors.
 */

import { useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { fetchSearchResults, type SearchResult } from '../../api/search'

interface Props {
  open: boolean
  onOpenChange: (open: boolean) => void
}

function resultHref(r: SearchResult): string {
  switch (r.kind) {
    case 'project':
      return `/projects?pm=${r.id}`
    case 'pay_app':
      return `/projects/${r.project_id}/pay-apps/${r.id}?step=1`
    case 'vendor':
      return r.vendor_user_id ? `/subs/${r.vendor_user_id}/trust` : '/subs'
    default:
      return '/dashboard'
  }
}

function kindLabel(kind: SearchResult['kind']): string {
  switch (kind) {
    case 'project': return 'Project'
    case 'pay_app': return 'Pay app'
    case 'vendor': return 'Sub / vendor'
    default: return ''
  }
}

export default function GlobalSearch({ open, onOpenChange }: Props) {
  const navigate = useNavigate()
  const inputRef = useRef<HTMLInputElement>(null)
  const [query, setQuery] = useState('')
  const [results, setResults] = useState<SearchResult[]>([])
  const [loading, setLoading] = useState(false)
  const [activeIndex, setActiveIndex] = useState(0)

  const close = useCallback(() => {
    onOpenChange(false)
    setQuery('')
    setResults([])
    setActiveIndex(0)
  }, [onOpenChange])

  const openResult = useCallback((result: SearchResult) => {
    navigate(resultHref(result))
    close()
  }, [navigate, close])

  useEffect(() => {
    if (!open) return
    const t = window.setTimeout(() => inputRef.current?.focus(), 0)
    return () => window.clearTimeout(t)
  }, [open])

  useEffect(() => {
    if (!open) return
    const q = query.trim()
    if (q.length < 2) {
      setResults([])
      setActiveIndex(0)
      return
    }

    let cancelled = false
    const timer = window.setTimeout(async () => {
      setLoading(true)
      try {
        const rows = await fetchSearchResults(q)
        if (!cancelled) {
          setResults(rows)
          setActiveIndex(0)
        }
      } catch {
        if (!cancelled) setResults([])
      } finally {
        if (!cancelled) setLoading(false)
      }
    }, 200)

    return () => {
      cancelled = true
      window.clearTimeout(timer)
    }
  }, [query, open])

  useEffect(() => {
    if (!open) return
    function onKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        e.preventDefault()
        close()
        return
      }
      if (!results.length) return
      if (e.key === 'ArrowDown') {
        e.preventDefault()
        setActiveIndex((i) => Math.min(i + 1, results.length - 1))
      } else if (e.key === 'ArrowUp') {
        e.preventDefault()
        setActiveIndex((i) => Math.max(i - 1, 0))
      } else if (e.key === 'Enter') {
        e.preventDefault()
        const hit = results[activeIndex]
        if (hit) openResult(hit)
      }
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [open, close, results, activeIndex, openResult])

  if (!open) return null

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Search"
      data-testid="global-search-modal"
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(9,9,11,.45)',
        backdropFilter: 'blur(2px)',
        zIndex: 700,
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'center',
        padding: '72px 16px 16px',
      }}
      onClick={close}
    >
      <div
        style={{
          width: '100%',
          maxWidth: 480,
          background: 'var(--surface, #fff)',
          border: '1px solid var(--line, #e4e4e7)',
          borderRadius: 'var(--r, 12px)',
          boxShadow: '0 20px 60px rgba(0,0,0,.18)',
          overflow: 'hidden',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={{ padding: '12px 14px', borderBottom: '1px solid var(--line, #e4e4e7)' }}>
          <div style={{ position: 'relative' }}>
            <span
              aria-hidden
              style={{
                position: 'absolute',
                left: 12,
                top: '50%',
                transform: 'translateY(-50%)',
                fontSize: 14,
                color: 'var(--ink4, #71717a)',
                pointerEvents: 'none',
              }}
            >
              🔍
            </span>
            <input
              ref={inputRef}
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search projects, pay apps, subs…"
              aria-label="Search query"
              data-testid="global-search-input"
              style={{
                width: '100%',
                border: '1px solid var(--line, #e4e4e7)',
                outline: 'none',
                fontSize: 14,
                fontFamily: 'inherit',
                background: 'var(--panel, var(--surface, #fff))',
                color: 'var(--ink, #111)',
                padding: '10px 12px 10px 34px',
                borderRadius: 'var(--rs, 8px)',
              }}
            />
          </div>
        </div>

        <div style={{ maxHeight: 360, overflowY: 'auto' }}>
          {loading && (
            <div style={{ padding: '14px 16px', fontSize: 12, color: 'var(--ink4, #71717a)' }}>
              Searching…
            </div>
          )}
          {!loading && query.trim().length < 2 && (
            <div style={{ padding: '14px 16px', fontSize: 12, color: 'var(--ink4, #71717a)' }}>
              Type at least 2 characters
            </div>
          )}
          {!loading && query.trim().length >= 2 && results.length === 0 && (
            <div style={{ padding: '14px 16px', fontSize: 12, color: 'var(--ink4, #71717a)' }}>
              No results
            </div>
          )}
          {!loading && results.map((row, index) => {
            const active = index === activeIndex
            return (
              <button
                key={`${row.kind}-${row.kind === 'pay_app' ? `${row.project_id}-${row.id}` : row.id}`}
                type="button"
                data-testid={`global-search-result-${index}`}
                onMouseEnter={() => setActiveIndex(index)}
                onClick={() => openResult(row)}
                style={{
                  width: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                  padding: '10px 14px',
                  border: 'none',
                  borderBottom: '1px solid var(--line, #f4f4f5)',
                  background: active ? 'var(--accent-l, #f4f4f5)' : 'transparent',
                  cursor: 'pointer',
                  textAlign: 'left',
                  fontFamily: 'inherit',
                }}
              >
                <span
                  style={{
                    fontSize: 10,
                    fontWeight: 600,
                    textTransform: 'uppercase',
                    letterSpacing: '.04em',
                    color: 'var(--ink4, #71717a)',
                    minWidth: 72,
                  }}
                >
                  {kindLabel(row.kind)}
                </span>
                <span style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--ink, #111)' }}>
                    {row.label}
                  </div>
                  {row.subtitle && (
                    <div style={{ fontSize: 11, color: 'var(--ink4, #71717a)', marginTop: 2 }}>
                      {row.subtitle}
                    </div>
                  )}
                </span>
              </button>
            )
          })}
        </div>

        <div
          data-testid="global-search-footer"
          style={{
            padding: '8px 14px',
            borderTop: '1px solid var(--line, #e4e4e7)',
            fontSize: 11,
            color: 'var(--ink4, #71717a)',
            display: 'flex',
            gap: 12,
          }}
        >
          <span>↵ open</span>
          <span>·</span>
          <span>esc close</span>
        </div>
      </div>
    </div>
  )
}

/** Clickable strip trigger — Cmd/Ctrl+K is handled in V3Shell. */
export function GlobalSearchTrigger({ onOpen }: { onOpen: () => void }) {
  return (
    <div
      className="search"
      role="button"
      tabIndex={0}
      data-testid="global-search-trigger"
      onClick={onOpen}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
          onOpen()
        }
      }}
    >
      🔍 Search projects, subs, docs <kbd>⌘K</kbd>
    </div>
  )
}
